import React, { useState, useEffect } from 'react';
import { 
  Layout, 
  Menu, 
  Button, 
  Input,
  Tabs, 
  Table, 
  Tooltip, 
  Modal, 
  message
} from 'antd';
import { 
  PlusOutlined, 
  DeleteOutlined, 
  ExclamationCircleOutlined, 
  SaveOutlined 
} from '@ant-design/icons';
import { withApollo } from 'react-apollo';
import gql from 'graphql-tag';
import { queryDepartmentsByCompanyIdIndex } from 'src/_shared/api/graphql/custom/departments/queryDepartmentsByCompanyIdGraphql.js';
import { querySubCompanyByCompanyIdIndex } from 'src/_shared/api/graphql/custom/sub-company/subCompanyByIdGraphql.js';
import { queryUserGroupsByCompanyIdIndex } from 'src/_shared/api/graphql/custom/userGroups/queryUserGroupsByCompanyIdGraphql.js';
import { queryUsersByCompanyIdIndex } from 'src/settings-dashboard/custom/users/getUsersByCompanyIdGraphql.js';
import { CreateFeatureFlag, DeleteFeatureFlag, GetFeatureFlagsByCompanyId, UpdateFeatureFlag } from 'src/_shared/api/graphql/custom/company/featureFlagQueriesMutationsGraphql.js';

const { Header, Sider, Content } = Layout;

const UserRole = {
  admin: 'admin',
  employee: 'employee',
  extendedUser: 'extendedUser',
  manager: 'manager',
  superAdmin: 'superAdmin',
  superUser: 'superUser',
  supportAdmin: 'supportAdmin'
};

const EntityTable = ({ 
  entities, 
  selectedIds, 
  onSelectionChange, 
  entityType,
  loading = false,
  pagination = false,
  onAddCustomId = null
}) => {
  const [customId, setCustomId] = useState('');

  const getDisplayName = (entity) => {
    switch(entityType) {
      case 'user':
        return `${entity.firstName || ''} ${entity.lastName || ''}`.trim() || entity.emailAddress || 'Unknown';
      case 'department':
      case 'subCompany':
      case 'userGroup':
        return entity.name || 'Unknown';
      case 'role':
        return entity;
      default:
        return entity.name || entity.id || 'Unknown';
    }
  };
  
  const getSecondaryInfo = (entity) => {
    switch(entityType) {
      case 'user':
        return entity.emailAddress || '';
      case 'department':
      case 'userGroup':
        return entity.active ? 'Active' : 'Inactive';
      default:
        return '';
    }
  };

  const columns = [];
  
  columns.push({
    title: entityType === 'role' ? 'Role' : 'ID',
    dataIndex: entityType === 'role' ? 'displayName' : 'id',
    key: entityType === 'role' ? 'role' : 'id',
    sorter: (a, b) => {
      const aValue = a[entityType === 'role' ? 'displayName' : 'id'] || '';
      const bValue = b[entityType === 'role' ? 'displayName' : 'id'] || '';
      return aValue.localeCompare(bValue);
    }
  });
  
  if (entityType !== 'role') {
    columns.push({
      title: entityType === 'user' ? 'Name/Email' : 'Name',
      dataIndex: 'displayName',
      key: 'displayName',
      sorter: (a, b) => {
        const aName = a.displayName || '';
        const bName = b.displayName || '';
        return aName.localeCompare(bName);
      }
    });

    columns.push({
      title: 'Info',
      dataIndex: 'info',
      key: 'info',
    });
  }

  let data = entities.map(entity => {
    if (entityType === 'role') {
      return {
        key: entity,
        id: entity,
        displayName: entity,
        role: entity
      };
    }
    
    return {
      key: entity.id,
      id: entity.id,
      displayName: getDisplayName(entity),
      info: getSecondaryInfo(entity),
      originalEntity: entity
    };
  });

  const manualIds = selectedIds.filter(id => !entities.some(entity => 
    entityType === 'role' ? entity === id : entity.id === id
  ));
  
  const manualData = manualIds.map(id => ({
    key: id,
    id: id,
    displayName: 'Custom ID',
    info: 'Manually added',
    isManual: true
  }));
  
  data = [...data, ...manualData];

  const handleAddCustomId = () => {
    if (customId.trim() && onAddCustomId) {
      onAddCustomId(customId.trim());
      setCustomId('');
    }
  };

  return (
    <>
      {entityType !== 'role' && onAddCustomId && (
        <div style={{ marginBottom: 16, display: 'flex' }}>
          <Input
            placeholder={`Enter ${entityType} ID`}
            value={customId}
            onChange={(e) => setCustomId(e.target.value)}
            style={{ maxWidth: 300, marginRight: 8 }}
            onPressEnter={handleAddCustomId}
          />
          <Button
            type="primary"
            onClick={handleAddCustomId}
            disabled={!customId.trim()}
          >
            Add
          </Button>
        </div>
      )}
      
      <Table
        rowSelection={{
          type: 'checkbox',
          selectedRowKeys: selectedIds,
          onChange: onSelectionChange
        }}
        columns={columns}
        dataSource={data}
        pagination={pagination ? { pageSize: 10 } : false}
        loading={loading}
        size="small"
        rowClassName={record => record.isManual ? 'custom-id-row' : ''}
      />
    </>
  );
};

const FeatureFlagManager = (props) => {
  const { 
    client,
    currentUser
  } = props;

  const companyId = currentUser?.companyId || currentUser?.company?.id;

  const [featureFlags, setFeatureFlags] = useState([]);
  const [selectedFlagIndex, setSelectedFlagIndex] = useState(null);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [newFlagCounter, setNewFlagCounter] = useState(1);
  
  const [departments, setDepartments] = useState([]);
  const [subCompanies, setSubCompanies] = useState([]);
  const [userGroups, setUserGroups] = useState([]);
  const [users, setUsers] = useState([]);
  const [roles] = useState(Object.values(UserRole));
  
  const [loading, setLoading] = useState(false);
  const [entityLoading, setEntityLoading] = useState({
    flags: false,
    departments: false,
    subCompanies: false,
    userGroups: false,
    users: false
  });
  
  const [nextTokens, setNextTokens] = useState({
    flags: null,
    departments: null,
    subCompanies: null,
    userGroups: null,
    users: null
  });

  useEffect(() => {
    if (companyId) {
      fetchFeatureFlags();
    }
  }, [companyId]);

  const fetchCompanyGroupData = async () => {
    setEntityLoading(prev => ({
      ...prev,
      departments: true,
      subCompanies: true,
      userGroups: true,
      users: true
    }));
    
    try {
      const { data: departmentsData } = await client.query({  
        query: gql(queryDepartmentsByCompanyIdIndex),
        variables: {
          companyId,
          limit: 1000,
          nextToken: null
        }
      });

      const { data: subCompaniesData } = await client.query({
        query: gql(querySubCompanyByCompanyIdIndex),
        variables: {
          companyId,
          limit: 1000,
          nextToken: null
        }
      });

      const { data: userGroupsData } = await client.query({
        query: gql(queryUserGroupsByCompanyIdIndex),
        variables: {
          companyId,
          limit: 1000,
          nextToken: null
        }
      });

      try {

      const { data: usersData } = await client.query({
        query: queryUsersByCompanyIdIndex,
        variables: {
          companyId,
          limit: 1000,
          nextToken: null
        }
      });

      setUsers(usersData.queryUsersByCompanyIdIndex.items);
      setNextTokens({
        ...nextTokens,
        users: usersData.queryUsersByCompanyIdIndex.nextToken
      });
      } catch {
        setUsers([]);
        setNextTokens({
            ...nextTokens,
            users: null
          });
      }

      setDepartments(departmentsData.queryDepartmentsByCompanyIdIndex.items);
      setSubCompanies(subCompaniesData.querySubCompanyByCompanyIdIndex.items);
      setUserGroups(userGroupsData.queryUserGroupsByCompanyIdIndex.items);
      

      setNextTokens({
        ...nextTokens,
        departments: departmentsData.queryDepartmentsByCompanyIdIndex.nextToken,
        subCompanies: subCompaniesData.querySubCompanyByCompanyIdIndex.nextToken,
        userGroups: userGroupsData.queryUserGroupsByCompanyIdIndex.nextToken,
      });
    } catch (error) {
      message.error('Failed to fetch entity data');
      console.error(error);
    } finally {
      setEntityLoading(prev => ({
        ...prev,
        departments: false,
        subCompanies: false,
        userGroups: false,
        users: false
      }));
    }
  };

  const fetchFeatureFlags = async () => {
    setEntityLoading(prev => ({ ...prev, flags: true }));
    
    try {
      const response = await client.query({
        query: GetFeatureFlagsByCompanyId,
        variables: {
          companyId: companyId,
          limit: 50,
          nextToken: null
        }
      });
      
      setFeatureFlags(response?.data?.getFeatureFlagsByCompanyId?.items || []);
      setNextTokens(prev => ({
        ...prev,
        flags: response?.data?.getFeatureFlagsByCompanyId?.nextToken || null
      }));
      
      await fetchCompanyGroupData();
    } catch (error) {
      message.error('Failed to fetch feature flags');
      console.error(error);
    } finally {
      setEntityLoading(prev => ({ ...prev, flags: false }));
    }
  };

  const addNewFeatureFlag = () => {
    const newFlag = {
      companyId,
      featureFlagName: `newFeatureFlag${newFlagCounter}`,
      byUserId: [],
      byRole: [],
      byDepartmentId: [],
      bySubCompanyId: [],
      byUserGroupId: [],
      isNew: true
    };
    
    setFeatureFlags([...featureFlags, newFlag]);
    setNewFlagCounter(newFlagCounter + 1);
    setSelectedFlagIndex(featureFlags.length);
    setHasUnsavedChanges(true);
  };

  const handleDeleteFlag = (index) => {
    const flagToDelete = featureFlags[index];
    
    if (!flagToDelete.isNew) {
      Modal.confirm({
        title: `Are you sure you want to delete ${flagToDelete.featureFlagName}?`,
        content: 'This cannot be undone.',
        okText: 'Yes, delete it',
        okType: 'danger',
        cancelText: 'Cancel',
        onOk: async () => {
          try {
            await client.mutate({
              mutation: DeleteFeatureFlag,
              variables: {
                companyId,
                featureFlagName: flagToDelete.featureFlagName
              }
            });
            
            const updatedFlags = [...featureFlags];
            updatedFlags.splice(index, 1);
            setFeatureFlags(updatedFlags);
            
            if (selectedFlagIndex === index) {
              setSelectedFlagIndex(null);
            } else if (selectedFlagIndex > index) {
              setSelectedFlagIndex(selectedFlagIndex - 1);
            }
            
            message.success(`Feature flag "${flagToDelete.featureFlagName}" deleted`);
          } catch (error) {
            message.error('Failed to delete feature flag');
            console.error(error);
          }
        }
      });
    } else {
      const updatedFlags = [...featureFlags];
      updatedFlags.splice(index, 1);
      setFeatureFlags(updatedFlags);
      
      if (selectedFlagIndex === index) {
        setSelectedFlagIndex(null);
      } else if (selectedFlagIndex > index) {
        setSelectedFlagIndex(selectedFlagIndex - 1);
      }
    }
  };

  const handleNameChange = (e) => {
    if (selectedFlagIndex === null) return;
    
    const updatedFlags = [...featureFlags];
    updatedFlags[selectedFlagIndex] = {
      ...updatedFlags[selectedFlagIndex],
      featureFlagName: e.target.value
    };
    
    setFeatureFlags(updatedFlags);
    setHasUnsavedChanges(true);
  };

  const saveFeatureFlag = async () => {
    if (selectedFlagIndex === null) return;
    
    const currentFlag = featureFlags[selectedFlagIndex];
    setLoading(true);
    
    try {
      if (currentFlag.isNew) {
        const input = {
          companyId,
          featureFlagName: currentFlag.featureFlagName,
          byUserId: currentFlag.byUserId,
          byRole: currentFlag.byRole,
          byDepartmentId: currentFlag.byDepartmentId,
          bySubCompanyId: currentFlag.bySubCompanyId,
          byUserGroupId: currentFlag.byUserGroupId
        };
        
        const response = await client.mutate({
          mutation: CreateFeatureFlag,
          variables: { input }
        });
        
        const updatedFlags = [...featureFlags];
        updatedFlags[selectedFlagIndex] = {
          ...response.data.createFeatureFlag,
          isNew: false
        };
        
        setFeatureFlags(updatedFlags);
        message.success(`Feature flag "${currentFlag.featureFlagName}" created`);
      } else {
        const input = {
          companyId,
          featureFlagName: currentFlag.featureFlagName,
          byUserId: currentFlag.byUserId,
          byRole: currentFlag.byRole,
          byDepartmentId: currentFlag.byDepartmentId,
          bySubCompanyId: currentFlag.bySubCompanyId,
          byUserGroupId: currentFlag.byUserGroupId
        };
        
        const response = await client.mutate({
          mutation: UpdateFeatureFlag,
          variables: { input }
        });
        
        const updatedFlags = [...featureFlags];
        updatedFlags[selectedFlagIndex] = {
          ...response.data.updateFeatureFlag,
          isNew: false
        };
        
        setFeatureFlags(updatedFlags);
        message.success(`Feature flag "${currentFlag.featureFlagName}" updated`);
      }
      
      setHasUnsavedChanges(false);
    } catch (error) {
      message.error('Failed to save feature flag');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEntitySelection = (group, selectedIds) => {
    if (selectedFlagIndex === null) return;
    
    const updatedFlags = [...featureFlags];
    updatedFlags[selectedFlagIndex] = {
      ...updatedFlags[selectedFlagIndex],
      [group]: selectedIds
    };
    
    setFeatureFlags(updatedFlags);
    setHasUnsavedChanges(true);
  };

  const handleAddCustomId = (group, id) => {
    if (selectedFlagIndex === null || !id) return;
    
    const updatedFlags = [...featureFlags];
    const currentIds = updatedFlags[selectedFlagIndex][group] || [];
    
    if (!currentIds.includes(id)) {
      updatedFlags[selectedFlagIndex] = {
        ...updatedFlags[selectedFlagIndex],
        [group]: [...currentIds, id]
      };
      
      setFeatureFlags(updatedFlags);
      setHasUnsavedChanges(true);
    }
  };

  const menuItems = featureFlags.map((flag, index) => ({
    key: index.toString(),
    label: (
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
        <span>{flag.featureFlagName}</span>
        <Button
          type="text"
          danger
          icon={<DeleteOutlined />}
          onClick={(e) => {
            e.stopPropagation();
            handleDeleteFlag(index);
          }}
        />
      </div>
    ),
    onClick: () => setSelectedFlagIndex(index)
  }));

  const tabItems = [
    {
      key: 'byRole',
      label: 'By Role',
      children: (
        <EntityTable
          entities={roles}
          selectedIds={selectedFlagIndex !== null ? featureFlags[selectedFlagIndex].byRole || [] : []}
          onSelectionChange={(selectedRowKeys) => handleEntitySelection('byRole', selectedRowKeys)}
          entityType="role"
          loading={entityLoading.flags}
        />
      )
    },
    {
      key: 'byUserId',
      label: 'By User ID',
      children: (
        <EntityTable
          entities={users}
          selectedIds={selectedFlagIndex !== null ? featureFlags[selectedFlagIndex].byUserId || [] : []}
          onSelectionChange={(selectedRowKeys) => handleEntitySelection('byUserId', selectedRowKeys)}
          entityType="user"
          loading={entityLoading.users}
          pagination={users.length > 10}
          onAddCustomId={(id) => handleAddCustomId('byUserId', id)}
        />
      )
    },
    {
      key: 'byDepartmentId',
      label: 'By Department ID',
      children: (
        <EntityTable
          entities={departments}
          selectedIds={selectedFlagIndex !== null ? featureFlags[selectedFlagIndex].byDepartmentId || [] : []}
          onSelectionChange={(selectedRowKeys) => handleEntitySelection('byDepartmentId', selectedRowKeys)}
          entityType="department"
          loading={entityLoading.departments}
          pagination={departments.length > 10}
          onAddCustomId={(id) => handleAddCustomId('byDepartmentId', id)}
        />
      )
    },
    {
      key: 'bySubCompanyId',
      label: 'By Sub Company ID',
      children: (
        <EntityTable
          entities={subCompanies}
          selectedIds={selectedFlagIndex !== null ? featureFlags[selectedFlagIndex].bySubCompanyId || [] : []}
          onSelectionChange={(selectedRowKeys) => handleEntitySelection('bySubCompanyId', selectedRowKeys)}
          entityType="subCompany"
          loading={entityLoading.subCompanies}
          pagination={subCompanies.length > 10}
          onAddCustomId={(id) => handleAddCustomId('bySubCompanyId', id)}
        />
      )
    },
    {
      key: 'byUserGroupId',
      label: 'By User Group ID',
      children: (
        <EntityTable
          entities={userGroups}
          selectedIds={selectedFlagIndex !== null ? featureFlags[selectedFlagIndex].byUserGroupId || [] : []}
          onSelectionChange={(selectedRowKeys) => handleEntitySelection('byUserGroupId', selectedRowKeys)}
          entityType="userGroup"
          loading={entityLoading.userGroups}
          pagination={userGroups.length > 10}
          onAddCustomId={(id) => handleAddCustomId('byUserGroupId', id)}
        />
      )
    }
  ];

  return (
    <Layout style={{ height: '100vh' }}>
      <Sider width={300} theme="light" className="feature-flag-sider">
        <div style={{ padding: '16px' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            block
            onClick={addNewFeatureFlag}
          >
            Add Feature Flag
          </Button>
        </div>
        <Menu
          mode="inline"
          selectedKeys={selectedFlagIndex !== null ? [selectedFlagIndex.toString()] : []}
          style={{ height: 'calc(100% - 64px)', overflow: 'auto' }}
          items={menuItems}
          className="feature-flag-menu"
        />
      </Sider>
      <Layout>
        <Header style={{ background: '#fff', padding: '0 16px', display: 'flex', alignItems: 'center' }}>
          {selectedFlagIndex !== null && (
            <>
              <Input
                placeholder="Feature Flag Name"
                value={featureFlags[selectedFlagIndex].featureFlagName}
                onChange={handleNameChange}
                style={{ width: 300, marginRight: 16 }}
              />
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={saveFeatureFlag}
                loading={loading}
              >
                Save
              </Button>
              {hasUnsavedChanges && (
                <Tooltip title="You have unsaved changes!">
                  <ExclamationCircleOutlined style={{ color: '#faad14', marginLeft: 8 }} />
                </Tooltip>
              )}
            </>
          )}
        </Header>
        <Content style={{ margin: '24px', background: '#fff', padding: 24, minHeight: 280 }}>
          {selectedFlagIndex !== null ? (
            <Tabs defaultActiveKey="byRole" items={tabItems} />
          ) : (
            <div style={{ textAlign: 'center' }}>
              <p>Select a feature flag from the sidebar or create a new one</p>
            </div>
          )}
        </Content>
      </Layout>
    </Layout>
  );
};

export default withApollo(FeatureFlagManager);
